// ── The active company, browser side (multi-company isolation, owner 2026-09-22) ─────────────────
// Which company this browser is working IN. For every staff member that is simply their own
// company and this module is inert; for a super admin it is whichever company they picked from the
// main menu. The choice rides on every API call as X-Active-Company, and the SERVER decides what it
// is worth: honoured only for a super admin and only for a real firm (services/companyScope.ts).
// Nothing here is a permission — it is the same shape as sandboxMode: a per-browser preference.
//
// PER BROWSER, per user: the key is suffixed with the user id, so a super admin's choice can never
// leak into a colleague's session on a shared machine (store.loadMe reconciles on every boot).
//
// Incident 2026-09-23 ("I picked a company, the page reloaded and I was back in SLF Legal"):
//   1. localStorage.setItem threw QuotaExceededError — the origin was full of draft mirrors — and
//      the catch below swallowed it, then reloaded anyway. Now the write makes room first
//      (draftMirror.setItemWithRoom), mirrors to sessionStorage, and REFUSES to reload when the
//      choice could not be kept anywhere: the caller shows why.
//   2. The user id was only known after /api/me answered, so the FIRST /api/me — the one the whole
//      shell derives its world from — went out without X-Active-Company. The JWT already carries
//      the user id (routes/auth.ts signs { userId, email }), so we bind from the token before any
//      request is made.

import { setItemWithRoom } from './draftMirror';

const KEY_PREFIX = 'mf_active_company:';

/** The two Samui firms — the one world every current operator lives in. Mirrors companyScope. */
export const SLF_LEGAL_ID = 'd97d5e65-8dd2-4f55-b0ac-38eeb08f925e';
export const SLF_ACCT_ID = '57c0f80a-5cb8-4312-aa46-c881db034886';
export const isSlfWorld = (companyId: string | null | undefined): boolean =>
  companyId === SLF_LEGAL_ID || companyId === SLF_ACCT_ID;

let current = '';
let userKey = '';

function read(): string {
  if (!userKey) return '';
  const k = KEY_PREFIX + userKey;
  try { const v = String(localStorage.getItem(k) || ''); if (v) return v; } catch { /* unavailable */ }
  try { return String(sessionStorage.getItem(k) || ''); } catch { return ''; }
}

/** The user id inside the JWT, without verifying it — we only need a storage key, and the
 *  server re-validates the company on every call regardless. Every role's token comes from the
 *  same signer (routes/auth.ts: { userId, email }), so a portal user binds to their own id too —
 *  harmless, they are never super. '' for a malformed token. */
export function userIdFromToken(token: string | null | undefined): string {
  try {
    const part = String(token || '').split('.')[1] || '';
    if (!part) return '';
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return String(JSON.parse(json)?.userId || '');
  } catch { return ''; }
}

/** Bind to the signed-in user (store.loadMe). Reloads the stored choice for that user. */
export function bindActiveCompanyUser(userId: string): void {
  userKey = String(userId || '');
  current = read();
}

/** Bind from the stored JWT before the first request goes out (store.loadMe, module init).
 *  A missing or malformed token binds to nobody — never keeps the previous user's binding. */
export function bindActiveCompanyFromToken(): void {
  let tok = '';
  try { tok = localStorage.getItem('mf_token') || ''; } catch { /* unavailable */ }
  bindActiveCompanyUser(userIdFromToken(tok));
}

export const getActiveCompanyId = (): string => current;

/** Persist the choice and reload: every mounted screen holds its own fetched data, and a company
 *  switch invalidates all of it at once — a reload is the honest way to say so.
 *  Returns false — and does NOT reload — when the choice could not be kept anywhere. */
export function setActiveCompany(companyId: string): boolean {
  const next = String(companyId || '');
  const k = KEY_PREFIX + userKey;
  let kept = false;
  if (next) {
    kept = setItemWithRoom(k, next);
    // If localStorage refused the new value, a stale one under the same key would shadow the
    // sessionStorage copy on the next read (read() prefers localStorage) — remove it first.
    if (!kept) { try { localStorage.removeItem(k); } catch { /* ignore */ } }
    try { sessionStorage.setItem(k, next); kept = true; } catch { /* private mode */ }
  } else {
    try { localStorage.removeItem(k); } catch { /* ignore */ }
    try { sessionStorage.removeItem(k); } catch { /* ignore */ }
    kept = true;
  }
  if (!kept) return false;
  current = next;
  try { window.location.reload(); } catch { /* tests */ }
  return true;
}

/** Drop a stored choice without reloading — used when /api/me says it is no longer valid. */
export function clearActiveCompany(): void {
  current = '';
  const k = KEY_PREFIX + userKey;
  try { localStorage.removeItem(k); } catch { /* private mode */ }
  try { sessionStorage.removeItem(k); } catch { /* ignore */ }
}

// Bind as early as the module loads, so even a request fired before store.loadMe carries the world.
bindActiveCompanyFromToken();
